import React, { ChangeEvent, useEffect, useState } from 'react';
import styles from './Textarea.module.scss';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';

interface TextareaProps extends React.ComponentProps<'textarea'> {
    label?: string;
    error?: string | null;
    footerActions?: React.ReactNode;
    required?: boolean;
    disabled?: boolean;
    className?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            // Specific props for the component
            label,
            error,
            footerActions,
            required,
            disabled,
            className,
            // Props that modify behavior
            value: initialValue,
            maxLength,
            onChange,
            // Native textarea props
            ...textareaProps
        },
        ref
    ) => {
        const [text, setText] = useState(initialValue as string);

        useEffect(() => {
            setText((initialValue ?? '') as string);
        }, [initialValue]);

        const hasValue = React.useMemo(() => (text?.length || 0) > 0, [text]);

        const showFooter = maxLength !== undefined || footerActions !== undefined;

        const remainingCharacters = React.useMemo(() => {
            if (maxLength === undefined) return 0;
            return maxLength - (text?.length || 0);
        }, [maxLength, text]);

        const handleChange = React.useCallback(
            (event: ChangeEvent<HTMLTextAreaElement>): void => {
                if (disabled) return;

                const newValue = event.target.value;
                setText(newValue);

                if (onChange) {
                    onChange(event);
                }
            },
            [disabled, onChange]
        );

        const textareaClasses = React.useMemo(
            () =>
                clsx(styles.textarea, className, {
                    [styles.textareaError]: error,
                    [styles.textareaFilled]: hasValue,
                    [styles.disabled]: disabled
                }),
            [error, hasValue, disabled, className]
        );

        const renderLabel = React.useMemo(() => {
            if (!label && !error) return null;
            return (
                <Label
                    required={required}
                    error={error}
                >
                    {label}
                </Label>
            );
        }, [label, required, error]);

        return (
            <div className={styles.container}>
                {renderLabel}
                <div
                    className={clsx(styles.textareaContainer, {
                        [styles.textareaContainerError]: error,
                        [styles.textareaContainerFilled]: hasValue
                    })}
                >
                    <textarea
                        ref={ref}
                        {...textareaProps}
                        disabled={disabled}
                        maxLength={maxLength}
                        value={text}
                        onChange={handleChange}
                        className={textareaClasses}
                    />

                    {showFooter && (
                        <div className={styles.textareaFooter}>
                            {footerActions}
                            {maxLength !== undefined && (
                                <span className={styles.textareaCounterText}>{remainingCharacters}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

Textarea.displayName = 'Textarea';

export { Textarea };
